import { createStyles, makeStyles } from '@material-ui/core/styles';
import { ReferralWiringInformationStore } from '../stores';
import { observer } from 'mobx-react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';
import React, { useRef, useState } from 'react';
import { Button, Paper, PaperBorderType } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import { TitleComp } from '../../../feature-borrowers/src/caf/components/titleComp';
import { formatDate, isCivicUrl } from '@roc/feature-utils';
import SignatureCanvas from 'react-signature-canvas';
import { ReferralWiringInformationSignatureFormPDF } from './referralWiringInformationSignatureFormPDF';

const useStyles = makeStyles(theme =>
  createStyles({
    sigCanvas: {
      width: '100%',
      height: 200,
      background: '#f7f7f7',
      border: '1px solid #aaa',
      borderRadius: 16,
    },
  })
);

export type ReferralWiringInformationSignatureFormProps = {
  store: ReferralWiringInformationStore;
};
export const ReferralWiringInformationSignatureForm = observer(
  (props: ReferralWiringInformationSignatureFormProps) => {
    const classes = useStyles();
    const { store } = props;
    const { userStore } = useStore();

    const { isLargeScreen } = useBorrowerPortalMediaQueries();
    const [isAgreed, setAgreed] = useState(false);
    const [isSigned, setSigned] = useState(false);
    const sigCanvas = useRef(null);
    const [signature, setSignature] = useState(null);

    const handleSign = () => {
      const signatureURL = sigCanvas.current
        .getTrimmedCanvas()
        .toDataURL('image/png');
      setSignature(signatureURL);
      store.sign(userStore.userInformation.userId, signatureURL);
    };

    const companyAbbreviated = isCivicUrl()
      ? 'CIVIC'
      : 'FACo';
    const companyName = isCivicUrl()
      ? 'CIVIC Financial Services'
      : 'Finance of America Commercial';

    return (
      <>
        <div id="screenshotParent" style={{ display: 'none' }}>
          <ReferralWiringInformationSignatureFormPDF
            store={store}
            signature={signature}
          />
        </div>
        <Grid
          item
          xs={12}
          style={{
            height: '90%',
          }}
        >
          <Grid container item xs={12}>
            <Grid item xs={12} sm={12}>
              <Grid item xs={12}>
                <Box mb={3}>
                  <Paper
                    variant="outlined"
                    style={{
                      maxHeight: isLargeScreen ? 300 : 150,
                      overflowY: 'auto',
                      padding: 24,
                      borderColor: '#ccc',
                    }}
                    borderType={PaperBorderType.SOLID_BORDER}
                  >
                    <Typography variant={'body1'}>
                      If the above account is not the undersigned entity’s
                      account, or if the above account has an account name/title
                      that is not the same as the name of the undersigned
                      entity, on behalf of the undersigned entity, I hereby
                      further expressly authorize the wire transfer, and deposit
                      of funds, in connection with the {companyAbbreviated}{' '}
                      Refer a Friend Program into such account by {companyName}{' '}
                      ("{companyAbbreviated}") or any agent or affiliate of{' '}
                      {companyAbbreviated}, and I hereby release{' '}
                      {companyAbbreviated} and all {companyAbbreviated}{' '}
                      affiliates and agents, and each of their respective
                      officers, managers, directors, consultants, agents,
                      advisors, and representatives, from all loss, damage,
                      liability, cost, or expense arising out of or resulting
                      from the deposit of funds into such account.
                    </Typography>

                    <Typography
                      variant={'body1'}
                      style={{ fontWeight: 'bold' }}
                    >
                      The foregoing authorization and wire instructions shall be
                      standing and shall remain in full force and effect until
                      such time as they are revoked or amended in writing by the
                      undersigned.
                    </Typography>

                    <Typography variant={'body1'}>
                      On behalf of the undersigned entity, I hereby acknowledge,
                      represent, and/or warrant as follows:
                    </Typography>
                    <ul>
                      <li>
                        <Typography variant={'body1'}>
                          I understand and acknowledge that neither Lender nor
                          any Lender Party shall be responsible for any
                          information, including any negative findings, which
                          may be contained in a Credit Report. I release Lender,
                          all Lender Parties, including its respective
                          employees, officers and owners, from any and all
                          liability for furnishing such information and
                          conducting such an investigation.
                        </Typography>
                      </li>

                      <li>
                        <Typography variant={'body1'}>
                          I am a duly authorized representative of the
                          undersigned entity and have been duly authorized by
                          such entity to execute this Standing Authorization and
                          Wire Instructions on behalf of such entity.
                        </Typography>
                      </li>

                      <li>
                        <Typography variant={'body1'}>
                          The above wire and account information is accurate and
                          correct and I acknowledge responsibility for any
                          errors resulting from incorrect/inaccurate information
                          provided.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          I authorize {companyAbbreviated} and any{' '}
                          {companyAbbreviated} agent or affiliate to effect
                          wires/transfers/deposits into the above specified
                          account in connection with the {companyAbbreviated}{' '}
                          Refer a Friend Program in accordance with the terms
                          and provisions of said Refer a Friend Program.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          {companyAbbreviated} reserves the right, on behalf of
                          itself and any agent or affiliate, to require
                          additional verification of the above specified account
                          information, whether written or oral, prior to
                          authorizing a wire/transfer/deposit, and such
                          additional verification may delay the execution of a
                          wire.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          The undersigned entity releases {companyAbbreviated}{' '}
                          and any affiliate, and each of their respective
                          officers, managers, directors, consultants, agents,
                          advisors, and representatives, from all loss, damage,
                          liability, cost, or expense (Loss) arising out of or
                          resulting from any action taken by{' '}
                          {companyAbbreviated} upon instructions provided under
                          this Standing Authorization and Wire Instructions,
                          unless the Loss arose out of {companyAbbreviated}’s
                          failure to exercise ordinary care, act in good faith,
                          or act in accordance with the wire instructions given
                          hereunder, and (2) agrees to indemnify and hold{' '}
                          {companyAbbreviated} and all {companyAbbreviated}{' '}
                          agents and affiliates harmless from any Loss arising
                          out of or resulting from any action taken by{' '}
                          {companyAbbreviated} or any {companyAbbreviated} agent
                          or affiliate in good faith upon instructions provided
                          under this Standing Authorization and Wire
                          Instructions.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          {companyAbbreviated} shall not be liable for refusing
                          to execute a wire transfer and deposit of funds
                          instruction based on {companyAbbreviated}’s reasonable
                          belief that such wire is violative of applicable law
                          or could cause {companyAbbreviated} to incur legal
                          liability for following such instructions.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          {companyAbbreviated} cannot guarantee the length of
                          time it takes for funds to be credited to the above
                          specified account after a wire is initiated.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          {companyAbbreviated} does not have any influence or
                          responsibility for fees or surcharges imposed by other
                          financial institutions involved in the transfer of
                          funds.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant={'body1'}>
                          THIS STANDING AUTHORIZATION AND WIRE INSTRUCTIONS
                          SHALL BE GOVERNED BY NEW YORK LAW.
                        </Typography>
                      </li>
                    </ul>
                  </Paper>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAgreed}
                        onChange={e => setAgreed(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box fontSize={18}>
                        I acknowledge that the information provided is correct.
                        <Box color={'red'} component={'span'} ml={0.5}>
                          *
                        </Box>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12}>
              <Grid xs={12}>
                <Box my={3}>
                  <TitleComp
                    title={
                      <span>
                        Signature
                        <Box color="red" component={'span'} ml={0.5}>
                          *
                        </Box>
                      </span>
                    }
                  />
                </Box>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <SignatureCanvas
                    penColor="green"
                    canvasProps={{ className: classes.sigCanvas }}
                    ref={sigCanvas}
                    onEnd={() => setSigned(true)}
                  />
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    width="100%"
                  >
                    <Button
                      testId={'sign'}
                      size="small"
                      onClick={() => {
                        // const dataURL = sigCanvas.current.toDataURL(
                        //   'image/png'
                        // );
                        // console.log(dataURL);
                        sigCanvas.current.clear();
                        setSigned(false);
                      }}
                    >
                      Clear
                    </Button>
                    <Box color={'blue'}>{formatDate(new Date())}</Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent={'space-between'}>
            <Grid item xs={6}>
              <Button
                variant={'contained'}
                testId={'cancel'}
                onClick={() => (store.activeStep = 0)}
                style={{
                  marginTop: 16,
                }}
              >
                Back
              </Button>
            </Grid>
            <Grid
              item
              xs={6}
              style={{
                textAlign: 'right',
              }}
            >
              <Button
                disabled={!(isSigned && isAgreed)}
                variant={'contained'}
                color={'primary'}
                testId={'confirm'}
                onClick={() => {
                  handleSign();
                }}
                style={{
                  marginTop: 16,
                }}
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }
);
